import { ChangeDetectionStrategy, Component, computed, input, Optional, signal } from '@angular/core';
import { toObservable } from '@angular/core/rxjs-interop';
import { connectState } from '@examdojo/angular/util';
import { IconComponent } from '@examdojo/core/icon';
import { isNotNullish } from '@examdojo/core/util/nullish';
import { isLocalQuestionImageUrl, isRemoteQuestionImageUrl, QuestionImage } from '@examdojo/models/question-image';
import { ImagePreviewWithButtonComponent } from '@examdojo/ui/image-preview';
import { MarkdownViewerComponent } from '@examdojo/ui/markdown-viewer';
import { TranslocoPipe } from '@jsverse/transloco';
import { catchError, distinctUntilChanged, filter, from, of, switchMap } from 'rxjs';
import { QuestionImageCacheService } from '../services/question-image-cache.service';
import { QuestionImageUploadService } from '../services/question-image-upload.service';

@Component({
  selector: 'dojo-question-image-viewer',
  template: `
    @if (hasValidImageContent()) {
      @if (isOriginal() || questionImage().not_to_scale || fillInTheBlanks()) {
        <div class="mb-2 flex items-center justify-center gap-2">
          @if (isOriginal()) {
            <span class="badge-above bg-sky-50 uppercase text-sky-600">{{ 'examdojo.original' | transloco }}</span>
          }
          @if (questionImage().not_to_scale) {
            <span class="badge-above bg-neutral-50 text-neutral-600">{{ 'examdojo.not_to_scale' | transloco }}</span>
          }
          @if (fillInTheBlanks()) {
            <span class="badge-above bg-neutral-50 text-neutral-600">{{
              'examdojo.fill_in_the_blanks' | transloco
            }}</span>
          }
        </div>
      }

      <div class="image-container relative flex w-full items-center justify-center" [style]="containerStyle()">
        @if (!state.signedUrl || isImageLoading()) {
          <div role="status" class="absolute flex h-full w-full animate-pulse items-center justify-center">
            <div class="flex h-full w-full items-center justify-center rounded-[0.5rem] bg-neutral-100">
              <dojo-icon [icon]="'image'" [size]="24" />
            </div>
          </div>
        }

        @if (state.signedUrl) {
          <dojo-image-preview-with-button
            [buttonOnly]="true"
            [src]="state.signedUrl"
            [hideButton]="isImageLoading()"
            class="h-full w-full"
          >
            <img
              style="border-radius: 12px;"
              class="max-h-full max-w-full object-contain"
              [class.invisible]="isImageLoading()"
              [src]="state.signedUrl"
              (load)="isImageLoading.set(false)"
            />
          </dojo-image-preview-with-button>
        }
      </div>

      @if (questionImage().title) {
        <div class="mt-2 text-center">
          <dojo-markdown-viewer
            class="image-title text-sm font-medium text-neutral-700"
            [content]="questionImage().title || ''"
            [applyProseClass]="false"
          />
        </div>
      }
    } @else {
      <div class="mt-2 flex w-full justify-center">
        <span class="text-gray-500">{{ 'examdojo.no_image' | transloco }}</span>
      </div>
    }
  `,
  imports: [TranslocoPipe, ImagePreviewWithButtonComponent, MarkdownViewerComponent, IconComponent],
  styles: [
    `
      :host {
        @apply flex max-h-[350px] w-full max-w-[600px] flex-col;
        overflow: hidden;
      }

      .badge-above {
        @apply rounded px-2 py-1 text-xs font-medium tracking-wide;
      }

      .image-title {
        @apply leading-relaxed;
        max-width: 100%;
        word-wrap: break-word;
      }

      .image-container {
        flex: 1;
        min-height: 200px;
        overflow: hidden;
      }
    `,
  ],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class QuestionImageViewerComponent {
  constructor(
    private readonly questionImageUploadService: QuestionImageUploadService,
    @Optional() private readonly questionImageCacheService?: QuestionImageCacheService,
  ) {}

  readonly questionImage = input.required<QuestionImage>();
  readonly fillInTheBlanks = input<boolean>(false);

  readonly isImageLoading = signal<boolean>(true);

  readonly isOriginal = computed(() => {
    const questionImage = this.questionImage();

    return !questionImage.content.url && questionImage.content.original_url;
  });

  readonly hasValidImageContent = computed(() => {
    const questionImage = this.questionImage();
    return !!(questionImage.content.url || questionImage.content.original_url);
  });

  readonly containerStyle = computed(() => {
    const customStyle = this.questionImage().style;

    return {
      ...customStyle,
      aspectRatio: '16 / 9',
    };
  });

  private readonly signedUrl$ = toObservable(this.questionImage).pipe(
    filter(isNotNullish),
    distinctUntilChanged(),
    switchMap((questionImage) => {
      this.isImageLoading.set(true);

      // Handle local main image URL
      if (questionImage.content.url && isLocalQuestionImageUrl(questionImage.content.url)) {
        const file = this.questionImageCacheService?.getLocalImage(questionImage.content.url);
        return of(file ? URL.createObjectURL(file) : null);
      }

      // Handle local original image URL (when no main URL exists)
      if (
        !questionImage.content.url &&
        questionImage.content.original_url &&
        isLocalQuestionImageUrl(questionImage.content.original_url)
      ) {
        const file = this.questionImageCacheService?.getLocalImage(questionImage.content.original_url);
        return of(file ? URL.createObjectURL(file) : null);
      }

      // Handle remote URLs (either main or original)
      const remoteUrl = questionImage.content.url ?? questionImage.content.original_url;
      if (remoteUrl && isRemoteQuestionImageUrl(remoteUrl)) {
        return from(this.questionImageUploadService.fetchSignedUrlFromImageUrl(remoteUrl)).pipe(
          catchError((err: unknown) => {
            console.error('Error getting signed URL:', err);
            return of(null);
          }),
        );
      }

      return of(null);
    }),
  );

  readonly state = connectState({
    signedUrl: this.signedUrl$,
  });
}
